// src/components/kuestion/shared/PreferencesContext.jsx
//
// GET / PATCH /api/me/preferences (Preferences & Timezone contract v1.2).
// One provider per signed-in shell, for either audience. It owns:
//   - the effective zone and clock, published to the display store so every
//     formatted time follows them (and a version bump so screens re-render)
//   - the theme, which follows the account once signed in ("system" tracks
//     prefers-color-scheme live)
//   - the device-zone hint, sent once per session and never shown
//   - the response table in §14.1, including the two 401s that mean
//     different things
//
// The routes answer a FLAT envelope ({status, timezone, …}), not {status, data},
// so this talks to fetch directly rather than through kuestionFetch.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { deviceZone, setDisplay, getDisplay } from './display';

const PrefsCtx = createContext(null);
export const usePreferences = () => useContext(PrefsCtx);

const DEFAULTS = { timezone: null, clock: '24', theme: 'system', locale: 'en-GB', org_default_timezone: null };
const DEBOUNCE_MS = 600;
const THEME_KEY = 'rw-theme';

const resolveTheme = (theme) => {
  if (theme === 'light' || theme === 'dark') return theme;
  try { return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'; } catch { return 'light'; }
};

export function PreferencesProvider({ token, onInvalidToken, children }) {
  // Until the server answers, the theme is whatever the sign-in page had
  // (rw-theme), so there is no flash to a different one at sign-in.
  const [prefs, setPrefs] = useState(() => {
    let saved = null;
    try { saved = localStorage.getItem(THEME_KEY); } catch { /* private mode */ }
    return { ...DEFAULTS, theme: saved === 'light' || saved === 'dark' ? saved : 'system' };
  });
  const [source, setSource] = useState('none'); // none | server | local
  const [version, setVersion] = useState(0);
  const pending = useRef({});
  const published = useRef(null);
  const timer = useRef(null);
  const hinted = useRef(false);

  const call = useCallback(async (method, body) => {
    const res = await fetch(API_ENDPOINTS.ME_PREFERENCES, {
      method,
      headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    let json = null;
    try { json = await res.json(); } catch { /* no body */ }
    return { status: res.status, json };
  }, [token]);

  const applyServer = useCallback((json) => {
    if (!json || json.status !== 'success') return;
    const next = {
      timezone: json.timezone ?? null,
      clock: json.clock === '12' ? '12' : '24',
      theme: ['light', 'dark', 'system'].includes(json.theme) ? json.theme : 'system',
      locale: json.locale || 'en-GB',
      org_default_timezone: json.org_default_timezone ?? null,
      updated_at: json.updated_at,
    };
    if (json.hint_ignored) console.info('preferences: the server ignored the device-zone hint'); // eslint-disable-line no-console
    setPrefs(next);
    setSource('server');
  }, []);

  // §14.1: what each answer means. Only invalid_token ends the session.
  const handle = useCallback((r, { hintOnly = false } = {}) => {
    if (r.status === 200) { applyServer(r.json); return true; }
    const reason = r.json?.reason;
    if (r.status === 401 && reason === 'invalid_token') { if (onInvalidToken) onInvalidToken(); return false; }
    if (r.status === 401) { console.warn('preferences: wrong_audience — client bug, session kept'); return false; } // eslint-disable-line no-console
    if (r.status === 404 || r.status === 0) { setSource((s) => (s === 'server' ? s : 'local')); return false; }
    if (r.status === 422 && hintOnly) return false; // a zone the server's tzdata does not know; nothing to do
    if (r.status === 422) console.error('preferences: rejected', reason); // eslint-disable-line no-console
    return false;
  }, [applyServer, onInvalidToken]);

  // On sign-in: read, then send the device hint. Hint-only, fire and forget.
  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    (async () => {
      try {
        const r = await call('GET');
        if (!live) return;
        handle(r);
        if (r.status === 200 && !hinted.current) {
          hinted.current = true;
          const dz = deviceZone();
          if (dz) call('PATCH', { detected_timezone: dz }).then((h) => live && handle(h, { hintOnly: true })).catch(() => {});
        }
      } catch { if (live) setSource((s) => (s === 'server' ? s : 'local')); }
    })();
    return () => { live = false; };
  }, [token, call, handle]);

  // Debounced, coalesced PATCH of only what changed. Optimistic locally.
  const flush = useCallback(async () => {
    const body = pending.current; pending.current = {};
    if (!Object.keys(body).length) return;
    try {
      const r = await call('PATCH', body);
      if (r.status === 429) {
        const wait = Math.max(1, Number(r.json?.retry_after) || 5) * 1000;
        pending.current = { ...body, ...pending.current };
        timer.current = setTimeout(flush, wait);
        return;
      }
      if (!handle(r) && r.status === 422) {
        // impossible from a picker; re-read the truth and revert the control
        const back = await call('GET'); handle(back);
      }
    } catch { /* offline: local state stands, retried on the next change */ }
  }, [call, handle]);

  const update = useCallback((patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    pending.current = { ...pending.current, ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, DEBOUNCE_MS);
  }, [flush]);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Publish zone + clock to the display store; bump the version so screens
  // that render times pick the change up.
  useEffect(() => {
    setDisplay({ timezone: prefs.timezone, orgDefault: prefs.org_default_timezone, clock: prefs.clock });
    // Bump only when what is rendered would differ, so a first read that
    // matches the defaults does not remount every screen once at sign-in.
    const sig = `${getDisplay().zone}|${prefs.clock}`;
    if (published.current !== null && published.current !== sig) setVersion((v) => v + 1);
    published.current = sig;
  }, [prefs.timezone, prefs.org_default_timezone, prefs.clock]);

  // Theme: the account's choice wins while signed in; "system" follows the OS live.
  useEffect(() => {
    const apply = () => {
      const t = resolveTheme(prefs.theme);
      document.documentElement.dataset.theme = t;
      try { localStorage.setItem(THEME_KEY, t); } catch { /* private mode */ }
    };
    apply();
    if (prefs.theme !== 'system' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => apply();
    if (mq.addEventListener) mq.addEventListener('change', on); else if (mq.addListener) mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else if (mq.removeListener) mq.removeListener(on); };
  }, [prefs.theme]);

  const value = useMemo(() => ({
    prefs, source, version,
    zone: getDisplay().zone,
    deviceZone: deviceZone(),
    resolvedTheme: resolveTheme(prefs.theme),
    setTimezone: (id) => update({ timezone: id }),      // null = back to detected
    setClock: (c) => update({ clock: c === '12' ? '12' : '24' }),
    setTheme: (t) => update({ theme: t }),
  }), [prefs, source, version, update]);

  return <PrefsCtx.Provider value={value}>{children}</PrefsCtx.Provider>;
}
